import { useState } from 'react';
import { ChevronLeft, Search, Phone, Mail, MessageCircle, HelpCircle } from 'lucide-react';
import { useFAQs } from '../../hooks/useAPI';

interface HelpSupportScreenProps {
  onNavigate: (screen: any) => void;
}

export default function HelpSupportScreen({ onNavigate }: HelpSupportScreenProps) {
  const { data: faqs, isLoading } = useFAQs();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);

  const filteredFaqs = faqs?.filter((faq: any) =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = [...new Set(faqs?.map((faq: any) => faq.category) || [])];

  return (
    <div className="w-full min-h-screen bg-background pb-24">
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => onNavigate('profile')}
            className="w-10 h-10 rounded-full hover:bg-secondary transition-colors flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg">Help & Support</h1>
            <p className="text-xs text-muted-foreground">FAQs and contact us</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 space-y-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search FAQs..."
            className="w-full h-12 pl-12 pr-4 rounded-xl bg-input-background border border-transparent focus:border-[#1A6B4F] focus:outline-none"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <a
            href="tel:+971501234567"
            className="bg-card border border-border rounded-xl p-4 text-center hover:bg-secondary transition-colors"
          >
            <Phone className="w-6 h-6 mx-auto mb-2 text-[#1A6B4F]" />
            <div className="text-xs">Call Us</div>
          </a>
          <a
            href="mailto:support@acerent.ae"
            className="bg-card border border-border rounded-xl p-4 text-center hover:bg-secondary transition-colors"
          >
            <Mail className="w-6 h-6 mx-auto mb-2 text-[#1A6B4F]" />
            <div className="text-xs">Email</div>
          </a>
          <a
            href="https://wa.me/971501234567?text=Hi%20AceRent%20support%2C%20I%20need%20help%20with..."
            target="_blank" rel="noopener noreferrer"
            className="bg-card border border-border rounded-xl p-4 text-center hover:bg-secondary transition-colors"
          >
            <MessageCircle className="w-6 h-6 mx-auto mb-2 text-[#1A6B4F]" />
            <div className="text-xs">WhatsApp</div>
          </a>
        </div>

        <div>
          <h2 className="text-lg mb-3">Frequently Asked Questions</h2>

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="bg-secondary rounded-xl p-4 h-12 animate-pulse" />
              ))}
            </div>
          ) : searchQuery && (!filteredFaqs || filteredFaqs.length === 0) ? (
            <div className="bg-card border border-border rounded-xl p-6 text-center text-sm text-muted-foreground">
              No FAQs match "{searchQuery}". Try a different keyword or reach out via WhatsApp above.
            </div>
          ) : (
            categories.map((category) => {
              const categoryFaqs = filteredFaqs?.filter((faq: any) => faq.category === category);
              if (!categoryFaqs || categoryFaqs.length === 0) return null;

              return (
                <div key={category} className="mb-6">
                  <h3 className="text-base mb-3 capitalize text-[#1A6B4F]">{category}</h3>
                  <div className="space-y-2">
                    {categoryFaqs.map((faq: any) => (
                      <div key={faq.id} className="bg-card border border-border rounded-xl overflow-hidden">
                        <button
                          onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                          className="w-full p-4 text-left flex items-start gap-3 hover:bg-secondary transition-colors"
                        >
                          <HelpCircle className="w-5 h-5 text-[#1A6B4F] flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="text-sm pr-4">{faq.question}</div>
                          </div>
                          <span className={`text-xl transition-transform flex-shrink-0 ${expandedFaq === faq.id ? 'rotate-180' : ''}`}>
                            ⌄
                          </span>
                        </button>
                        {expandedFaq === faq.id && (
                          <div className="px-4 pb-4 pl-12">
                            <p className="text-sm text-muted-foreground leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="bg-gradient-to-br from-[#0F1E36] to-[#16294A] rounded-2xl p-6 text-white">
          <h3 className="text-lg mb-2">Still need help?</h3>
          <p className="text-sm opacity-90 mb-4">
            Our support team is available 24/7 to assist you with any questions or concerns.
          </p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>+971 50 123 4567</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>support@acerent.ae</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              <span>WhatsApp: +971 50 123 4567</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
